import { LightButton, LightRoutingLink, PrimaryButton } from "components/buttons/buttons";
import PasswordControl from "components/controls/password-control/password-control";
import PhoneControl from "components/controls/phone-control/phone-control";
import TextControl from "components/controls/text-control/text-control";
import partial from "lodash/partial";
import { useEffect } from "react";
import { QuestionCircleFill } from "react-bootstrap-icons";
import { useForm } from "react-hook-form";
import NumericControl from "../../../../components/controls/numeric-control/numeric-control";
import { conditionalChaining, nullary } from "../../../../core/utils";
import { obtenerNombreComercio, useRouting } from "../../../../hooks/with-routing";
import { RestablecerClaveSteps } from "../pages/restablecer-clave.page";

type RestablecerClaveForma = {
	usuario: string;
	telefono: string;
	codigoOtp: string;
	clave: string;
	confirmacionClave: string;
};

type RestablecerClaveFormProps = {
	cargandoDatos?: boolean;
	step: RestablecerClaveSteps;
	iniciales?: string;
	obtenerIniciales: (username: string) => void;
	enviarCodigo: (username: string, phone: string) => void;
	reenviarCodigo: (username: string, phone: string) => void;
	restablecerClave: (username: string, code: string, otp: string) => void;
	reiniciar: VoidFunction;
};

const RestablecerClaveForm = ({ cargandoDatos, step, iniciales, obtenerIniciales, enviarCodigo, reenviarCodigo, restablecerClave, reiniciar }: RestablecerClaveFormProps) => {
	const methods = useForm<RestablecerClaveForma>({ defaultValues: { clave: "", confirmacionClave: "" } });
	const config = useRouting.use.configuration();
	const createPath = useRouting.use.createPath();
	const imageUrl = createPath("/Usuarios/Configuracion/:segmento/Logo");

	const continueHandler = (form: RestablecerClaveForma) => {
		if (step === RestablecerClaveSteps.Inicio) {
			obtenerIniciales(form.usuario);
		}
		if (step === RestablecerClaveSteps.Captura) {
			enviarCodigo(form.usuario, form.telefono);
		}
		if (step === RestablecerClaveSteps.Registro) {
			restablecerClave(form.usuario, form.clave, form.codigoOtp);
		}
	};

	const resendCodeHandler = () => {
		const { usuario, telefono } = methods.getValues();

		methods.trigger(["usuario", "telefono"])
			.then(conditionalChaining)
			.then(nullary(partial(reenviarCodigo, usuario, telefono)))
	};

	const resetHandler = () => {
		methods.reset();
		reiniciar();
	};

	useEffect(() => {
		if (step === RestablecerClaveSteps.Inicio) {
			methods.setFocus("usuario");
		}
		if (step === RestablecerClaveSteps.Registro) {
			methods.setFocus("clave");
		}
	}, [step, methods]);

	return (
		<form>
			<div className="card-login-title">
				<img src={imageUrl} alt="alt" height="45px" />
				<div className="col-12">
					<span className="text-uppercase">Restablece tu Contraseña</span>
				</div>
			</div>
			{step === RestablecerClaveSteps.Inicio &&
				<>
					<div className="row ">
						<div className="mb-3">
							<TextControl
								name="usuario"
								label="Usuario"
								maxLength={15}
								formMethods={methods}
								pattern={{
									message: "Sólo permite letras, números y algunos caracteres especiales ( & # * - _ )",
									value: /^([A-Z0-9&#*\-_]{8,15})$/i
								}}
								minLength={{
									message: "La longitud debe ser igual o mayor a 8 caracteres",
									value: 8
								}}
								required
							/>
							<span data-gloss={`Ingresa el usuario proporcionado por ${obtenerNombreComercio(config)}`} className="tags icon-login">
								<QuestionCircleFill width="25" height="25" className="tooltip-icon" />
							</span>
						</div>
					</div>
					<div className="row">
						<div className="mb-3">
							<PhoneControl
								name="telefono"
								label="Teléfono"
								formMethods={methods}
								required
							/>
							<span data-gloss="Captura tu teléfono" className="tags icon-login">
								<QuestionCircleFill width="25" height="25" className="tooltip-icon" />
							</span>
						</div>
					</div>
					<div className="col-12 mb-3 text-center">
						<PrimaryButton disabled={cargandoDatos} onClick={methods.handleSubmit(continueHandler)}>Continuar</PrimaryButton>
					</div>
					<div className="mb-3 text-center">
						<LightRoutingLink disabled={cargandoDatos} className="link-banorte" to="/:segmento/login">Iniciar sesión</LightRoutingLink>
					</div>
				</>
			}
			{step === RestablecerClaveSteps.Captura &&
				<>
					<div className="mb-3 mt-3">
						<span className="label-initials">{iniciales}</span>
					</div>
					<div className="mb-3">
						<span className="label-login">Si tus iniciales son correctas, captura una contraseña para continuar el restablecimiento:</span>
					</div>
					<div className="row ">
						<div className="mb-3">
							<PasswordControl
								name="clave"
								label="Contraseña"
								maxLength={25}
								formMethods={methods}
								pattern={{
									message: "Debe contener al menos un número, una letra mayúscula, una letra minúscula y un carácter especial ( @ # $ % & ! * )",
									value: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@#$%&!*])([a-zA-Z0-9@#$%&!*]{12,25})$/
								}}
								minLength={{
									message: "La longitud debe ser entre 12 y 25 caracteres",
									value: 12
								}}
								required
							/>
							<span
								data-gloss="Al generar tu contraseña ten en cuenta lo siguiente:
*La contraseña y la confirmación de esta debe ser la misma
*La contraseña debe tener una longitud entre 12 y 25 caracteres, sin espacios
*La contraseña debe contener un número, una letra mayúscula, una letra minúscula y un carácter especial ( @ # $ % & ! * )"
								className="tags icon-login">
								<QuestionCircleFill width="25" height="25" className="tooltip-icon" />
							</span>
						</div>
					</div>
					<div className="row">
						<div className="mb-3">
							<PasswordControl
								name="confirmacionClave"
								label="Confirma contraseña"
								maxLength={25}
								formMethods={methods}
								compareTo={{
									fieldToCompare: "clave",
									message: "¡La contraseña no coincide! Por favor intenta de nuevo"
								}}
								required
							/>
						</div>
					</div>
					<div className="mb-3 d-grid">
					</div>
					<div className="col-12 mb-3 text-center">
						<LightButton className="me-5" disabled={cargandoDatos} onClick={resetHandler}>No soy yo</LightButton>
						<PrimaryButton disabled={cargandoDatos} onClick={methods.handleSubmit(continueHandler)}>Continuar</PrimaryButton>
					</div>
					<div className="mb-3 text-center">
						<LightRoutingLink disabled={cargandoDatos} className="link-banorte" to="/:segmento/login">Iniciar sesión</LightRoutingLink>
					</div>
				</>
			}
			{step === RestablecerClaveSteps.Registro &&
				<>
					<div className="mb-3 mt-3">
						<span className="label-initials">{iniciales}</span>
					</div>
					<div className="mb-3">
						<span className="label-login">Ingresa el código que te hemos enviado vía SMS</span>
					</div>
					<div className="row ">
						<div className="mb-3">
							<NumericControl
								name="codigoOtp"
								label="Código"
								allowLeadingZeros
								maxLength={6}
								formMethods={methods}
								minLength={{
									message: "La longitud debe ser igual a 6 dígitos",
									value: 6
								}}
								required
							/>
						</div>
					</div>
					<div className="mb-3 d-grid">
					</div>
					<div className="col-12 mb-3 text-center">
						<LightButton className="me-5" disabled={cargandoDatos} onClick={resendCodeHandler}>Reenviar código</LightButton>
						<PrimaryButton disabled={cargandoDatos} onClick={methods.handleSubmit(continueHandler)}>Finalizar</PrimaryButton>
					</div>
					<div className="mb-3 text-center">
						<LightRoutingLink disabled={cargandoDatos} className="link-banorte" to="/:segmento/login">Iniciar sesión</LightRoutingLink>
					</div>
				</>
			}
		</form>
	);
};

export default RestablecerClaveForm;